import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    BarChart3,
    Banknote,
    Calendar,
    ClipboardList,
    CreditCard,
    Eye,
    Info,
    List as ListIcon,
    QrCode,
    Receipt,
    Wallet,
    Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import StatusChip from '../../component/common/StatusChip';
import ServiceOrderViewSheet from '../../component/sheet/ServiceOrderViewSheet';
import DefaultTable from '../../component/table/DefaultTable';
import PaymentMixPanel from '../../component/report/PaymentMixPanel';
import usePermissions from '../../hooks/usePermissions';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { parseMoney } from '../../component/common/MobileDashboardKit';
import { protoIcon } from '../../component/common/ProtoIcon';
import MobileReport, { MobileReportBody, formatMoneyKpi, formatMoneyRank, useMobileRegenerate } from '../../component/report/MobileReport';
import { AreaLine, Caption, ChartCard, DonutBlock, HBarsLimited } from '../../component/report/MobileSalesSections';
import { OrderList } from '../../component/report/MobileOrderSections';
import { KitSeg } from '../../component/common/MobileKitExtras';
import { KitEmptyNote } from '../../component/common/MobileDashboardKit';

/** Valores monetários formatados pela API (ex.: "1.234,56") → número para somas de KPI. */
const parseApiMoneyToNumber = (value) => {
    if (value == null || value === '') return 0;
    const normalized = String(value).trim().replace(/\./g, '').replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
};

const formatBrlNumber = (n) =>
    Number(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Rótulo compacto para o eixo Y dos gráficos de barra em R$ (ex. "R$ 6,2k")
// — igual a `SalesReport.js`.
const formatCompactCurrency = (value) => {
    if (value === null || value === undefined) return '';
    const abs = Math.abs(value);
    if (abs >= 1000) {
        return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// A API pode responder em snake_case ou PascalCase dependendo do endpoint —
// mesma tolerância que a tela já tinha antes do redesign.
const pickOsField = (row, snake, pascal) => row[snake] ?? row[pascal] ?? '';

const osMoneyDisplay = (row, snake, pascal) => {
    const raw = pickOsField(row, snake, pascal);
    const str = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '0,00';
    return str;
};



// Domínio de forma de pagamento — mesmos enums crus do backend
// (`DINHEIRO`/`DÉBITO`/`CRÉDITO`/`PIXKEY`/`PIXQRCODE`, ver
// `fv-api/report/orderservice.go`). `StatusChip domain="paymentMethod"`
// (DESIGN-SYSTEM.md §4) só cobre dinheiro/débito/crédito/pix — reduz-se
// PIXKEY/PIXQRCODE a "pix" antes de repassar o valor, igual a `SalesReport.js`.
const PIX_RAW_TYPES = new Set(['PIXKEY', 'PIXQRCODE']);
const PAYMENT_FRIENDLY_LABEL = {
    DINHEIRO: 'Dinheiro',
    'DÉBITO': 'Débito',
    'CRÉDITO': 'Crédito',
    PIXKEY: 'Pix (Chave)',
    PIXQRCODE: 'Pix (QR Code)',
};
const paymentDomainValue = (raw) => (PIX_RAW_TYPES.has(raw) ? 'pix' : raw);
const paymentFriendlyLabel = (raw) => PAYMENT_FRIENDLY_LABEL[raw] || raw;

/** Mesmo lookup de tom que `StatusChip domain="paymentMethod"` usa internamente
 * (DESIGN-SYSTEM.md §4), reaproveitado aqui para colorir gráficos de
 * pagamento com as mesmas cores dos chips da tabela — cópia local de
 * `SalesReport.js`. */
function getPaymentTone(theme, raw) {
    const key = paymentDomainValue(raw);
    switch (key) {
        case 'DINHEIRO':
            return { fg: theme.palette.success.main, bg: theme.palette.success.bg };
        case 'DÉBITO':
            return { fg: theme.palette.info.main, bg: theme.palette.info.bg };
        case 'CRÉDITO':
            return { fg: theme.palette.secondary.main, bg: theme.palette.secondary.bg };
        case 'pix':
            return { fg: theme.palette.brand2.main, bg: theme.palette.info.bg };
        default:
            return { fg: theme.palette.text.disabled, bg: theme.palette.background.subtle };
    }
}

const PAYMENT_ICON = {
    DINHEIRO: Banknote,
    'DÉBITO': CreditCard,
    'CRÉDITO': CreditCard,
    pix: QrCode,
};

// `.pay-chip` — ícone 22px tintado + rótulo, igual a `SalesReport.js`.
function PaymentChip({ raw }) {
    const theme = useTheme();
    const tone = getPaymentTone(theme, raw);
    const Icon = PAYMENT_ICON[paymentDomainValue(raw)] || Banknote;
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{
                width: 22,
                height: 22,
                borderRadius: '7px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: tone.bg,
                color: tone.fg,
            }}>
                <Icon size={12} strokeWidth={2} />
            </Box>
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>
                {paymentFriendlyLabel(raw)}
            </Typography>
        </Box>
    );
}

/** Mesmos tons semânticos de `StatusChip domain="orderService"`
 * (DESIGN-SYSTEM.md §4: "Paga" é relabelado para "Faturada"), reaproveitados
 * para colorir o donut "Total de ordens x status" com as mesmas cores dos
 * chips da tabela/lista de OS (`pages/service_order/List.js`). */
function getOrderServiceStatusTone(theme, label) {
    const key = (label || '').toLowerCase();
    if (key === 'finalizada' || key === 'paga' || key === 'faturada') return theme.palette.success.main;
    if (key === 'cancelada' || key === 'estornada') return theme.palette.error.main;
    if (key === 'em execucao' || key === 'em execução') return theme.palette.info.main;
    if (key === 'nao iniciada' || key === 'não iniciada') return theme.palette.warning.main;
    return theme.palette.text.disabled;
}

function orderServiceStatusFriendlyLabel(raw) {
    return raw === 'Paga' ? 'Faturada' : raw;
}

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta, dentro de um
// painel de gráfico) — cópia local de `SalesReport.js`.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// `.legend-item` — dot + label + valor em negrito, usado sob os donuts.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11.5px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// Painel de gráfico genérico (`.panel-head`) — cópia local de `SalesReport.js`.
function ChartPanel({ title, subtitle, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={160} height={20} />
                            <Skeleton variant="text" width={200} height={14} />
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                            {subtitle ? (
                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                            ) : null}
                        </>
                    )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// Gráfico de linha/área ("Crescimento mensal de ordens") — DESIGN-SYSTEM.md
// §5.9 "Inline SVG polyline + gradient area | LineChart (area variant)",
// mesma técnica de `SalesReport.js`/`OrderDashboard.js`, mas em tom roxo
// (`var(--purple)` = `theme.palette.secondary.main`), igual ao protótipo
// (`prototipo-relatorios.html`, seção "RELATÓRIO DE ORDENS DE SERVIÇO").
const AreaLineChart = ({ dataset, xKey, yKey, title, subtitle, loading, height = 260 }) => {
    const theme = useTheme();
    const hasData = Array.isArray(dataset) && dataset.length > 0;

    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : hasData ? (
                    <LineChart
                        dataset={dataset}
                        height={height - 20}
                        margin={{ top: 16, right: 16, bottom: 24, left: 8 }}
                        hideLegend
                        grid={{ horizontal: true }}
                        xAxis={[{
                            scaleType: 'point',
                            dataKey: xKey,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: yKey,
                            label: title,
                            color: theme.palette.secondary.main,
                            area: true,
                            showMark: dataset.length <= 31,
                            valueFormatter: (value) => `${Number(value || 0).toLocaleString('pt-BR')} ordem(ns)`,
                        }]}
                        sx={{
                            '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                            '& .MuiAreaElement-root': { fill: 'url(#serviceOrderReportMonthlyAreaGradient)' },
                            '& .MuiMarkElement-root': {
                                fill: theme.palette.background.paper,
                                stroke: theme.palette.secondary.main,
                                strokeWidth: 2,
                            },
                        }}
                    >
                        <defs>
                            <linearGradient id="serviceOrderReportMonthlyAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={theme.palette.secondary.main} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={theme.palette.secondary.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                    </LineChart>
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Ajuste o período acima para visualizar os dados" />
                )}
            </Box>
        </ChartPanel>
    );
};

// Gráfico de barras genérico (contagem ou total em R$ por forma de
// pagamento/cliente) — cópia local de `SalesReport.js`.
const CategoryBarChart = ({ data, xKey, title, subtitle, loading, height = 240, currency = false }) => {
    const theme = useTheme();
    const dataKey = data && data[0] ? Object.keys(data[0]).find((key) => key === 'count' || key === 'total') : null;

    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : data && data.length > 0 && dataKey ? (
                    <BarChart
                        dataset={data}
                        height={height - 20}
                        margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                        borderRadius={6}
                        hideLegend
                        xAxis={[{
                            scaleType: 'band',
                            dataKey: xKey,
                            categoryGapRatio: 0.5,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{
                            disableLine: true,
                            disableTicks: true,
                            width: currency ? 56 : undefined,
                            valueFormatter: currency ? (value) => formatCompactCurrency(value) : undefined,
                        }]}
                        series={[{
                            dataKey,
                            color: theme.palette.secondary.main,
                            valueFormatter: (value) => currency ? `R$ ${formatBrlNumber(value)}` : `${value} ordem(ns)`,
                        }]}
                        sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                    />
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem registros no período selecionado" />
                )}
            </Box>
        </ChartPanel>
    );
};

// Donut (PieChart real via @mui/x-charts) com overlay central + legenda em
// linhas — cópia local de `SalesReport.js`.
const DonutPanel = ({ title, subtitle, data, centerValue, centerCaption, loading, emptyTitle, emptySubtitle }) => {
    const hasData = data.some((item) => item.value > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Skeleton variant="circular" width={128} height={128} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Box>
            ) : hasData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 128, height: 128 }}>
                        <PieChart
                            series={[{
                                data,
                                innerRadius: 38,
                                outerRadius: 64,
                                paddingAngle: 2,
                                cornerRadius: 3,
                            }]}
                            height={128}
                            width={128}
                            slotProps={{ legend: { hidden: true } }}
                            tooltip={{ trigger: 'item' }}
                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <Box sx={{
                            position: 'absolute',
                            inset: 18,
                            borderRadius: '50%',
                            bgcolor: 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>
                                {centerValue}
                            </Typography>
                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>{centerCaption}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                        {data.map((item, index) => (
                            <LegendRow key={`${item.label}-${index}`} color={item.color} label={item.label} value={item.displayValue ?? item.value} />
                        ))}
                    </Box>
                </Box>
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </ChartPanel>
    );
};

// Campo do card mobile: rótulo pequeno + valor; valor zerado fica em cinza discreto.
function OsCardField({ label, value, muted = false, strong = false }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '11px', lineHeight: 1.3, color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{
                fontSize: strong ? '14px' : '13px',
                fontWeight: strong ? 800 : 600,
                lineHeight: 1.35,
                fontVariantNumeric: 'tabular-nums',
                color: muted ? 'text.disabled' : (strong ? 'primary.main' : 'text.primary'),
                overflowWrap: 'anywhere',
            }}>
                R$ {value}
            </Typography>
        </Box>
    );
}

// Card mobile da listagem de OS (usado via `mobileCardRenderer` do DefaultTable).
// Cabeçalho: data + status + ações; destaque: total c/ desconto (+ total);
// grupo "Custos" em grade 2 colunas; forma de pagamento ao final.
function ServiceOrderMobileCard({ row, menu, onRowClick }) {
    const isZero = (v) => parseApiMoneyToNumber(v) === 0;
    const total = osMoneyDisplay(row, 'total', 'Total');
    const totalWithDiscount = osMoneyDisplay(row, 'total_with_discount', 'TotalWithDiscount');
    const productsCost = osMoneyDisplay(row, 'products_cost', 'ProductsCost');
    const externalCost = osMoneyDisplay(row, 'external_service_cost', 'ExternalServiceCost');
    const totalCost = osMoneyDisplay(row, 'total_cost', 'TotalCost');
    const paymentTypes = row.type_payment?.split(',').map((t) => t.trim()).filter(Boolean) || [];

    return (
        <Box
            onClick={() => onRowClick && onRowClick(row)}
            sx={{
                p: '12px',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                cursor: onRowClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 700, color: 'text.primary' }}>
                    {row.date_created}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                    <StatusChip domain="orderService" value={row.status} />
                </Box>
                {menu}
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                columnGap: 1.5,
                rowGap: '6px',
                p: '8px 10px',
                borderRadius: '8px',
                bgcolor: 'background.subtle',
            }}>
                <OsCardField label="Total c/ desconto" value={totalWithDiscount} strong />
                <OsCardField label="Total" value={total} muted={isZero(total)} />
            </Box>

            <Box>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>
                    Custos
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.5, rowGap: '6px' }}>
                    <OsCardField label="Peças" value={productsCost} muted={isZero(productsCost)} />
                    <OsCardField label="Terceirizado" value={externalCost} muted={isZero(externalCost)} />
                    <OsCardField label="Custo total" value={totalCost} muted={isZero(totalCost)} />
                </Box>
            </Box>

            {paymentTypes.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {paymentTypes.map((type, index) => (
                        <PaymentChip key={index} raw={type} />
                    ))}
                </Stack>
            )}
        </Box>
    );
}

// Card único que agrupa os indicadores de custo/desconto do período (antes eram
// três `KpiTickerCard`). Valor: tendência (`data.total`, centavos) quando existe,
// senão o total calculado da lista.
function CostIndicatorsPanel({ loading, items }) {
    const fmtCents = (cents) => `R$ ${formatBrlNumber((cents ?? 0) / 100)}`;
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '10px 16px', '&:last-child': { pb: '10px' } }}>
                {/* <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary', lineHeight: '20px', mb: '2px' }}>Custos e descontos</Typography> */}
                {items.map((it, index) => {
                    const cents = it.data ? it.data.total : it.value;
                    return (
                        <Box
                            key={it.key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                py: '6px',
                                borderTop: index === 0 ? 'none' : '1px solid',
                                borderColor: 'divider',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            <Typography sx={{ fontSize: '13px', lineHeight: '20px', color: 'text.secondary', minWidth: 0, whiteSpace: 'nowrap' }}>
                                {it.label}
                            </Typography>
                            {loading ? (
                                <Skeleton variant="text" width={90} height={20} />
                            ) : (
                                <Typography sx={{ fontSize: '15px', fontWeight: 800, lineHeight: '20px', color: 'text.primary', whiteSpace: 'nowrap' }}>
                                    {cents == null ? '—' : fmtCents(cents)}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </CardContent>
        </Card>
    );
}

const MONTH_ABBR_LABELS =['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function ServiceOrderReport() {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [orderViewSheetOpen, setOrderViewSheetOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [reportData, setReportData] = useState(null);
    // Tendência dos KPIs (`POST /chart/orderservice/kpi-trend`); falha não derruba o relatório.
    const [kpiTrend, setKpiTrend] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    const fetchReportData = async () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        setLoading(true);
        try {
            const trendRequest = httpClient.post('/chart/orderservice/kpi-trend', {
                start: startDate.format('DD/MM/YYYY'),
                end: endDate.format('DD/MM/YYYY'),
            })
                .then((res) => res.data)
                .catch(() => null);
            const response = await httpClient.post('/chart/orderservice', {
                start_month: startDate.format('DD/MM/YYYY'),
                end_month: endDate.format('DD/MM/YYYY')
            });

            const listOrderService =
                response.data.ListOrderService ??
                response.data.list_order_service ??
                [];

            setKpiTrend(await trendRequest);
            setReportData({
                ...response.data,
                ListOrderService: listOrderService
            });
        } catch (error) {
            console.error('Erro ao buscar dados do relatório:', error);
            setDialogMessage('Erro ao gerar relatório. Tente novamente.');
            setOpenDialog(true);
        } finally {
            setLoading(false);
        }
    };

    // Filtros (mobile): volta ao período padrão sem gerar o relatório.
    const handleClearFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
    };

    // Deep-link (`?open=true&start=DD/MM/YYYY&end=DD/MM/YYYY`) preservado do
    // comportamento anterior a este redesign — nenhuma tela hoje aponta para
    // cá com esses parâmetros, mas mantido por preservação de comportamento.
    useEffect(() => {
        const url = new URL(window.location.href);
        const open = url.searchParams.get('open');
        const startParam = url.searchParams.get('start');
        const endParam = url.searchParams.get('end');

        if (startParam && endParam) {
            setStartDate(dayjs(startParam, 'DD/MM/YYYY'));
            setEndDate(dayjs(endParam, 'DD/MM/YYYY'));
        }

        if (open === 'true') {
            setTimeout(() => {
                fetchReportData();
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExport = () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        const url = `/chart/orderservice/export?start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`;
        window.open(url, '_blank');
    };

    // Preparar dados para gráficos
    const getServiceOrderYearData = () => {
        if (!reportData?.OrderServiceYear) return [];
        const values = reportData.OrderServiceYear.Values ?? reportData.OrderServiceYear.values;
        if (!Array.isArray(values)) return [];
        return MONTH_ABBR_LABELS.map((label, index) => ({
            month: label,
            count: values[index] || 0
        }));
    };

    const getServiceOrderByPaymentData = () => {
        if (!reportData?.OrderServiceTypePayment) return [];
        return reportData.OrderServiceTypePayment.Labels.map((label, index) => ({
            payment: paymentFriendlyLabel(label),
            count: reportData.OrderServiceTypePayment.Values[index] || 0,
            rawLabel: label,
        }));
    };

    const getServiceOrderByPaymentTotalData = () => {
        if (!reportData?.OrderServiceTotalTypePayment) return [];
        return reportData.OrderServiceTotalTypePayment.Labels.map((label, index) => ({
            payment: paymentFriendlyLabel(label),
            total: reportData.OrderServiceTotalTypePayment.Values[index] || 0,
        }));
    };

    const getServiceOrderStatusData = () => {
        if (!reportData?.OrderServiceTotalStatus) return [];
        return reportData.OrderServiceTotalStatus.Labels.map((label, index) => ({
            id: index,
            value: reportData.OrderServiceTotalStatus.Values[index] || 0,
            label: orderServiceStatusFriendlyLabel(label),
            color: getOrderServiceStatusTone(theme, label),
        }));
    };

    // Mantido comentado por preservação de comportamento — já estava
    // desativado (sem gráfico correspondente na aba Gráficos) antes deste
    // redesign, só a versão em R$ (`getServiceOrderAmountByCustomerData`)
    // está ativa.
    // const getServiceOrderByCustomerData = () => {
    //     if (!reportData?.CountOSByCustomer) return [];
    //     return reportData.CountOSByCustomer.Labels.map((label, index) => ({
    //         label,
    //         value: reportData.CountOSByCustomer.Values[index] || 0
    //     }));
    // };

    const getServiceOrderAmountByCustomerData = () => {
        if (!reportData?.TotalAmountOSByCustomer) return [];
        return reportData.TotalAmountOSByCustomer.Labels.map((label, index) => ({
            customer: label,
            total: reportData.TotalAmountOSByCustomer.Values[index] || 0
        }));
    };

    const costPeriodKpis = useMemo(() => {
        const list = reportData?.ListOrderService;
        if (!Array.isArray(list) || list.length === 0) {
            return { products: 0, external: 0, total: 0 };
        }
        return list.reduce(
            (acc, row) => ({
                products: acc.products + parseApiMoneyToNumber(pickOsField(row, 'products_cost', 'ProductsCost')),
                external: acc.external + parseApiMoneyToNumber(pickOsField(row, 'external_service_cost', 'ExternalServiceCost')),
                total: acc.total + parseApiMoneyToNumber(pickOsField(row, 'total_cost', 'TotalCost'))
            }),
            { products: 0, external: 0, total: 0 }
        );
    }, [reportData]);

    const costColumnTitle = (label, tooltip) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <span>{label}</span>
            <Tooltip title={tooltip} arrow placement="top">
                <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled', cursor: 'help' }}>
                    <Info size={12} strokeWidth={2} />
                </Box>
            </Tooltip>
        </Box>
    );

    const renderMoneyRight = (row, snake, pascal) => (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', display: 'block', width: '100%', textAlign: 'right' }}>
            R$ {osMoneyDisplay(row, snake, pascal)}
        </Typography>
    );

    const listData = reportData?.ListOrderService || [];
    const statusDonutData = getServiceOrderStatusData();
    const totalStatusCount = statusDonutData.reduce((sum, item) => sum + item.value, 0);
    const netCents = Math.round(parseMoney(reportData?.TotalAmountNet) * 100);

    const isMobileShell = useIsMobileShell();
    // Mobile: gera ao abrir e a cada troca de período/filtros (mesma chamada e payload do botão "Gerar").
    // O deep-link `?open=true` já dispara a geração sozinho; aqui só entram as trocas do usuário.
    const regenerate = useMobileRegenerate(fetchReportData, { enabled: isMobileShell });

    if (isMobileShell) {
        const net = parseMoney(reportData?.TotalAmountNet);
        const activeOrders = listData.filter((row) => (row.status || '').toLowerCase() !== 'cancelada');
        const ticket = activeOrders.length > 0 ? net / activeOrders.length : 0;
        const canViewOrder = hasPermission('serviceorder.view');
        const openOrder = (row) => {
            setSelectedOrderId(row.id);
            setOrderViewSheetOpen(true);
        };
        const cancelledCount = statusDonutData.filter((item) => (item.label || '').toLowerCase() === 'cancelada').reduce((sum, item) => sum + item.value, 0);
        const cancelPct = totalStatusCount > 0 ? Math.round((cancelledCount / totalStatusCount) * 100) : 0;
        const biggest = [...listData]
            .filter((row) => (row.status || '').toLowerCase() !== 'cancelada')
            .sort((a, b) => parseApiMoneyToNumber(pickOsField(b, 'total_with_discount', 'TotalWithDiscount')) - parseApiMoneyToNumber(pickOsField(a, 'total_with_discount', 'TotalWithDiscount')))
            .slice(0, 3);
        const maxStatus = Math.max(1, ...statusDonutData.map((item) => item.value));

        // Mesmos totais do desktop (`KpiCard`s), exibidos por extenso até 99.999,99.
        const moneyKpi = (raw) => {
            const n = parseMoney(raw);
            return Math.abs(n) >= 100000 ? formatMoneyKpi(n) : `R$ ${formatBrlNumber(n)}`;
        };
        const totalKpis = [
            { label: 'Total em Dinheiro', icon: 'dollar', value: moneyKpi(reportData?.TotalAmountOSCash) },
            { label: 'Total em Débito', icon: 'card', value: moneyKpi(reportData?.TotalAmountOSDebit) },
            { label: 'Total em Crédito', icon: 'wallet', value: moneyKpi(reportData?.TotalAmountOSCredit) },
            { label: 'Total em Pix', icon: 'scan', value: moneyKpi(reportData?.TotalAmountOSPixQrCode) },
            { label: 'Total Bruto', icon: 'tag', value: moneyKpi(reportData?.TotalAmountGross) },
            { label: 'Total Líquido', icon: 'check', value: moneyKpi(reportData?.TotalAmountNet) },
        ];
        const costKpis = [
            { label: 'Custo de peças', icon: 'package', value: `R$ ${formatBrlNumber(costPeriodKpis.products)}` },
            { label: 'Custo terceirizado', icon: 'users', value: `R$ ${formatBrlNumber(costPeriodKpis.external)}` },
            { label: 'Custo total', icon: 'dollar', value: `R$ ${formatBrlNumber(costPeriodKpis.total)}` },
        ];

        // Lista: mesma ordem padrão do desktop (data de criação, decrescente).
        const sortedOrders = [...listData].sort((a, b) => {
            const av = String(a.date_created ?? '').toLowerCase();
            const bv = String(b.date_created ?? '').toLowerCase();
            return av > bv ? -1 : av < bv ? 1 : 0;
        });
        const getListRow = (row) => {
            const total = osMoneyDisplay(row, 'total', 'Total');
            const productsCost = osMoneyDisplay(row, 'products_cost', 'ProductsCost');
            const externalCost = osMoneyDisplay(row, 'external_service_cost', 'ExternalServiceCost');
            const totalCost = osMoneyDisplay(row, 'total_cost', 'TotalCost');
            return {
                date: row.date_created,
                status: row.status,
                statusLabel: orderServiceStatusFriendlyLabel(row.status),
                total: `R$ ${total}`,
                totalZero: parseApiMoneyToNumber(total) === 0,
                totalWithDiscount: `R$ ${osMoneyDisplay(row, 'total_with_discount', 'TotalWithDiscount')}`,
                productsCost: `R$ ${productsCost}`,
                productsCostZero: parseApiMoneyToNumber(productsCost) === 0,
                externalCost: `R$ ${externalCost}`,
                externalCostZero: parseApiMoneyToNumber(externalCost) === 0,
                totalCost: `R$ ${totalCost}`,
                totalCostZero: parseApiMoneyToNumber(totalCost) === 0,
                payments: (row.type_payment?.split(',').map((t) => t.trim()).filter(Boolean) || []).map(paymentFriendlyLabel).join(', '),
            };
        };

        const yearItems = getServiceOrderYearData().map((r) => ({ label: r.month, value: Number(r.count) || 0 }));
        const peakMonth = yearItems.reduce((best, it) => (it.value > (best?.value ?? -1) ? it : best), null);
        const byPaymentCount = getServiceOrderByPaymentData().map((r) => ({ name: r.payment, value: Number(r.count) || 0, text: String(r.count) }));
        const byPaymentTotal = getServiceOrderByPaymentTotalData().map((r) => ({ name: r.payment, value: Number(r.total) || 0, text: `R$ ${formatBrlNumber(r.total)}` }));
        const byCustomer = getServiceOrderAmountByCustomerData().map((r) => ({ name: r.customer, value: Number(r.total) || 0, text: `R$ ${formatBrlNumber(r.total)}` }));
        const noRecords = <KitEmptyNote>Sem registros no período selecionado</KitEmptyNote>;

        const chartsNode = (
            <>
                <ChartCard title="Crescimento mensal de ordens" subtitle="Ordens faturadas por mês (jan–dez)">
                    {yearItems.length > 0 ? (
                        <>
                            <AreaLine items={yearItems} />
                            {peakMonth && peakMonth.value > 0 && (
                                <Caption>Maior mês: <strong>{peakMonth.label}</strong> · {peakMonth.value.toLocaleString('pt-BR')} ordem(ns)</Caption>
                            )}
                        </>
                    ) : <KitEmptyNote>Nenhum dado disponível</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Total de ordens x status" bare>
                    <DonutBlock
                        segments={statusDonutData.map((item) => ({ value: item.value, color: item.color }))}
                        center={totalStatusCount.toLocaleString('pt-BR')}
                        legend={statusDonutData.map((item) => ({ label: `${item.label} · ${item.value.toLocaleString('pt-BR')}`, color: item.color }))}
                        empty="Sem ordens no período selecionado"
                    />
                </ChartCard>

                <ChartCard title="Ordens x forma de pagamento (qtd.)">
                    {byPaymentCount.length > 0 ? <HBarsLimited items={byPaymentCount} /> : noRecords}
                </ChartCard>

                <ChartCard title="Ordens (R$) x forma de pagamento">
                    {byPaymentTotal.length > 0 ? <HBarsLimited items={byPaymentTotal} valueWidth={96} /> : noRecords}
                </ChartCard>

                <ChartCard title="Total de ordens (R$) x cliente">
                    {byCustomer.length > 0 ? <HBarsLimited items={byCustomer} valueWidth={96} /> : noRecords}
                </ChartCard>
            </>
        );

        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Lista de ordens' }, { value: 1, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>
                    {activeTab === 0 ? (
                        <OrderList
                            key={sortedOrders.length}
                            rows={sortedOrders}
                            getRow={getListRow}
                            onOpen={canViewOrder ? openOrder : undefined}
                        />
                    ) : chartsNode}
                </Box>
            </Box>
        );

        return (
            <PageScaffold title="Relatório de Ordens de Serviço" titlePageTarget="Ordens de Serviço" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                    exportItems={[{ label: 'Excel (CSV)', icon: protoIcon('download'), onClick: handleExport }]}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(reportData)}
                        kpis={[
                            { label: 'Ordens', icon: 'orders', value: listData.length.toLocaleString('pt-BR') },
                            { label: 'Faturado', icon: 'dollar', grad: true, value: formatMoneyKpi(net) },
                            { label: 'Ticket médio', icon: 'tag', value: formatMoneyKpi(ticket) },
                            { label: 'Canceladas', icon: 'ban', value: cancelledCount.toLocaleString('pt-BR'), delta: { text: cancelledCount > 0 ? `${cancelPct}%` : '', tone: 'down' } },
                        ]}
                        sections={[
                            { type: 'kpis', title: 'Totais do período', items: totalKpis },
                            { type: 'kpis', title: 'Indicadores de custo no período (ordens faturadas)', items: costKpis },
                            { type: 'hbars', title: 'Por status', items: statusDonutData.map((item) => ({ name: item.label, value: item.value / maxStatus, text: String(item.value) })), empty: 'Nenhuma ordem no período' },
                            {
                                type: 'rank',
                                title: 'Maiores ordens',
                                rows: biggest.map((row) => ({
                                    title: `OS · ${String(row.date_created || '').slice(0, 10)}`,
                                    sub: orderServiceStatusFriendlyLabel(row.status),
                                    value: formatMoneyRank(parseApiMoneyToNumber(pickOsField(row, 'total_with_discount', 'TotalWithDiscount'))),
                                })),
                                empty: 'Nenhuma ordem no período',
                            },
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
                <ServiceOrderViewSheet
                    open={orderViewSheetOpen}
                    onClose={() => {
                        setOrderViewSheetOpen(false);
                        setSelectedOrderId(null);
                    }}
                    orderId={selectedOrderId}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Ordens de Serviço"
            titlePageTarget="Ordens de Serviço"
            links={[{ title: "Relatórios", link: "/reports" }]}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + ação primária
                        (Exportar CSV), igual a `prototipo-relatorios.html`. */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 2.5,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '11px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                background: (t) => t.palette.gradient.brand,
                            }}>
                                <Wrench size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Ordens de Serviço
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Volume, custos e status das ordens de serviço no período.
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    {/* filter-bar (sem card) — período + ações (Limpar / Exportar / Gerar). */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={fetchReportData}
                        loading={loading}
                        onClear={handleClearFilters}
                        exportItems={[{ label: 'Ordens de serviço', description: 'Todas as ordens do período (CSV)', onClick: handleExport }]}
                    />

                    {/* Chips de filtro ativo + contagem no período. */}
                    <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                        <Chip
                            icon={<Calendar size={13} strokeWidth={2} />}
                            label={`Período: ${startDate?.format('DD/MM/YYYY')} – ${endDate?.format('DD/MM/YYYY')}`}
                            size="small"
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                        {reportData && (
                            <Chip
                                icon={<ClipboardList size={13} strokeWidth={2} />}
                                label={`${listData.length} ordem${listData.length === 1 ? '' : 's'} no período`}
                                size="small"
                                sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                            />
                        )}
                    </FilterChips>

                    {/* Conteúdo do relatório */}
                    {reportData ? (
                        <>
                            {/* KPIs estilo bolsa de valores (`KpiTickerCard`) — ref.
                                `ui-ux/prototipo-relatorio-os-kpis.html`. Grid de 12 colunas:
                                3 largos (4) + 4 médios (3) em lg; 2 por linha em sm; 1 em xs.
                                Custos e descontos são "ruins quando sobem" (`inverse`).
                                Sem a tendência (falha do endpoint) os cards mostram só o que
                                `/chart/orderservice` já devolve. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
                                {/* <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Indicadores do período</Typography> */}
                                {kpiTrend?.days?.length > 0 && (
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>
                                        {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia' : 'dias'} · vs. {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia anterior' : 'dias anteriores'}
                                    </Typography>
                                )}
                            </Box>
                            {/* Forma de pagamento (60%) + card com os indicadores de custo/desconto (40%);
                                empilham abaixo de `lg`. Valores de `order_service_payment` das OS
                                criadas no período (Dinheiro/Débito/Crédito/Pix); % sobre a soma deles. */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(340px, auto)' },
                                gap: '14px',
                                alignItems: 'start',
                                mb: '14px',
                                '& > :first-of-type > .MuiCard-root': { mb: 0 },
                            }}>
                            <Box sx={{ minWidth: 0 }}>
                            <PaymentMixPanel
                                loading={loading}
                                subtitle="Participação por forma de pagamento nas ordens do período"
                                shareLabel="dos pagamentos"
                                totalGross={parseMoney(reportData.TotalAmountGross)}
                                totalNet={parseMoney(reportData.TotalAmountNet)}
                                items={[
                                    { key: 'cash', label: 'Dinheiro', color: theme.palette.success.main, value: parseMoney(reportData.TotalAmountOSCash) },
                                    { key: 'debit', label: 'Débito', color: theme.palette.primary.main, value: parseMoney(reportData.TotalAmountOSDebit) },
                                    { key: 'credit', label: 'Crédito', color: theme.palette.secondary.main, value: parseMoney(reportData.TotalAmountOSCredit) },
                                    { key: 'pix', label: 'Pix', color: theme.palette.brand2.main, value: parseMoney(reportData.TotalAmountOSPixQrCode) },
                                ]}
                            />
                            </Box>

                            <CostIndicatorsPanel
                                loading={loading}
                                items={[
                                    { key: 'external', label: 'Custo terceirizado', data: kpiTrend?.external_cost, value: Math.round(costPeriodKpis.external * 100) },
                                    { key: 'discounts', label: 'Descontos concedidos', data: kpiTrend?.discounts },
                                    { key: 'total', label: 'Custo total', data: kpiTrend?.total_cost, value: Math.round(costPeriodKpis.total * 100) },
                                ]}
                            />
                            </Box>

                            {/* seg-tabs — Lista de ordens / Gráficos (DESIGN-SYSTEM.md §9
                                "ToggleButtonGroup restyled", igual a `SalesReport.js`). */}
                            <Box sx={{ mb: 2.25 }}>
                                <ToggleButtonGroupTabs activeTab={activeTab} onChange={setActiveTab} />
                            </Box>

                            {activeTab === 0 && (
                                <Box>
                                    <DefaultTable
                                        colunms={[
                                            { path: 'id', name: 'ID', invisible: true },
                                            { path: 'date_created', name: 'Data' },
                                            {
                                                path: 'total',
                                                name: 'Total',
                                                render: (row) => (
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>
                                                        R$ {row.total}
                                                    </Typography>
                                                )
                                            },
                                            {
                                                path: 'total_with_discount',
                                                name: 'Total c/ desconto',
                                                render: (row) => (
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                                                        R$ {row.total_with_discount}
                                                    </Typography>
                                                )
                                            },
                                            {
                                                path: 'products_cost',
                                                name: costColumnTitle('Custo peças', 'Custo de compra dos produtos lançados na ordem (não é o valor cobrado do cliente).'),
                                                render: (row) => renderMoneyRight(row, 'products_cost', 'ProductsCost')
                                            },
                                            {
                                                path: 'external_service_cost',
                                                name: costColumnTitle('Custo terc.', 'Quanto custou ao negócio o serviço feito por terceiro, conforme cadastro do serviço.'),
                                                render: (row) => renderMoneyRight(row, 'external_service_cost', 'ExternalServiceCost')
                                            },
                                            {
                                                path: 'total_cost',
                                                name: costColumnTitle('Custo total', 'Soma do custo de peças e do custo de serviço externo nesta ordem.'),
                                                render: (row) => renderMoneyRight(row, 'total_cost', 'TotalCost')
                                            },
                                            {
                                                path: 'type_payment',
                                                name: 'Pagamento',
                                                render: (row) => {
                                                    const paymentTypes = row.type_payment?.split(',').map((t) => t.trim()).filter(Boolean) || [];
                                                    if (paymentTypes.length === 0) {
                                                        return <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>—</Typography>;
                                                    }
                                                    return (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                                                            {paymentTypes.map((type, index) => (
                                                                <PaymentChip key={index} raw={type} />
                                                            ))}
                                                        </Stack>
                                                    );
                                                }
                                            },
                                            {
                                                path: 'status',
                                                name: 'Status',
                                                render: (row) => <StatusChip domain="orderService" value={row.status} />
                                            },
                                        ]}
                                        data={listData}
                                        iconsActions={hasPermission('serviceorder.view') ? [{
                                            icon: <Eye size={16} strokeWidth={2} />,
                                            props: {
                                                onClick: (row) => {
                                                    setSelectedOrderId(row.id);
                                                    setOrderViewSheetOpen(true);
                                                },
                                                tooltip: 'Visualizar ordem de serviço',
                                            }
                                        }] : []}
                                        mobileCardRenderer={(row, { menu, onRowClick }) => (
                                            <ServiceOrderMobileCard row={row} menu={menu} onRowClick={onRowClick} />
                                        )}
                                        loading={loading}
                                        rowPerPage={10}
                                        itemLabel="ordens"
                                        sortableFields={['date_created', 'status']}
                                        defaultSort={{ field: 'date_created', direction: 'desc' }}
                                    />
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* charts-grid — Crescimento mensal / Status (prototipo-relatorios.html). */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <AreaLineChart
                                            dataset={getServiceOrderYearData()}
                                            xKey="month"
                                            yKey="count"
                                            title="Crescimento mensal de ordens"
                                            subtitle="Ordens faturadas por mês (jan–dez)"
                                            loading={loading}
                                        />
                                        <DonutPanel
                                            title="Total de ordens x status"
                                            data={statusDonutData}
                                            centerValue={totalStatusCount.toLocaleString('pt-BR')}
                                            centerCaption="ordens"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem ordens no período selecionado"
                                        />
                                    </Box>

                                    {/* charts-grid — Pagamento (qtd. / R$), igual ao protótipo. */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <CategoryBarChart
                                            data={getServiceOrderByPaymentData()}
                                            xKey="payment"
                                            title="Ordens x forma de pagamento (qtd.)"
                                            loading={loading}
                                        />
                                        <CategoryBarChart
                                            data={getServiceOrderByPaymentTotalData()}
                                            xKey="payment"
                                            title="Ordens (R$) x forma de pagamento"
                                            loading={loading}
                                            currency
                                        />
                                    </Box>

                                    {/* Extra não coberto pelo protótipo simplificado, mas mantido
                                        por preservação de comportamento (já existia antes deste
                                        redesign). */}
                                    <CategoryBarChart
                                        data={getServiceOrderAmountByCustomerData()}
                                        xKey="customer"
                                        title="Total de ordens (R$) x cliente"
                                        loading={loading}
                                        currency
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.disabled',
                                }}>
                                    <Wrench size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>
                                    Nenhum relatório gerado
                                </Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Gerar relatório" para visualizar os dados das ordens de serviço.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Erro"
                iconType="error"
                content={dialogMessage}
            />

            <ServiceOrderViewSheet
                open={orderViewSheetOpen}
                onClose={() => {
                    setOrderViewSheetOpen(false);
                    setSelectedOrderId(null);
                }}
                orderId={selectedOrderId}
            />
        </PageScaffold>
    );
}

// seg-tabs — `ToggleButtonGroup` restyled globalmente em `theme.js`, com as
// medidas próprias do protótipo (track 4px de padding/gap e 11px de raio,
// botão 9px/18px de padding, 8px de raio, 12.5px/700 de fonte) — mesma
// sobrescrita local de `SalesReport.js`.
const segTabButtonSx = {
    gap: '7px',
    padding: '9px 18px',
    lineHeight: 'normal',
    borderRadius: '8px !important',
    fontSize: '12.5px',
    fontWeight: 700,
};

function ToggleButtonGroupTabs({ activeTab, onChange }) {
    return (
        <ToggleButtonGroup
            exclusive
            value={activeTab}
            onChange={(e, v) => (v || v === 0) && onChange(v)}
            sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px' }}
        >
            <ToggleButton value={0} sx={segTabButtonSx}>
                <ListIcon size={14} strokeWidth={2} /> Lista de ordens
            </ToggleButton>
            <ToggleButton value={1} sx={segTabButtonSx}>
                <BarChart3 size={14} strokeWidth={2} /> Gráficos
            </ToggleButton>
        </ToggleButtonGroup>
    );
}
